import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  ICellRendererParams,
} from "ag-grid-community";
import {
  TablePagination,
  TableContainer,
  TableContent,
  TableHeader,
  TableHeaderProps,
  TablePaginationProps,
} from "@cdkglobal/radial";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../../constants";
import {
  getPageSummaryText,
  getRowsSummaryText,
  numberFirstSort,
} from "../../helpers";
import {
  RowData,
  RowDataReducerState,
  RowDataValueType,
} from "../../../../types";
import { useIntl } from "react-intl";
import ActionCellRenderer from "../ActionCellRenderer";
import TableHeaderContent from "../custom-renderers/TableHeaderContent";
import ExportRecordsDialog from "../table-actions/ExportRecordsDialog";
import ARMigrationToast from "../table-actions-setup-based/ARMigrationToast";
import TruncatedCellRenderer from "../../../common/TruncatedCellRenderer";
import { useUIStateContext } from "../../../../contexts/UIStateContext";

interface SetupAgGridTableProps {
  rowDataReducerState: RowDataReducerState;
  languageSpecificColDefs: ColDef[];
  dataToDisplay: RowData[];
  tableHeaderProps: TableHeaderProps;
  renderField: (
    fieldName: string,
    fieldValue: RowDataValueType,
    rowData: RowData,
  ) => React.ReactNode;
  getFieldDisplayValue: (
    fieldName: string,
    rowData: RowData,
  ) => RowDataValueType;
  handleView: (rowData: RowData) => void;
  handleEdit: (rowData: RowData) => void;
  handleCopy: (rowData: RowData) => Promise<void>;
  handleDelete: (rowData: RowData) => void;
  isEditDisabled: boolean;
  isDeleteDisabled: boolean;
  isSetupActionsLocked: boolean;
  gridFields: Set<string>;
  setupName: string;
  currentPage: number;
  setCurrentPage: (p: number) => void;
  isExportDialogOpen: boolean;
  headerLabel: string;
}

export default function SetupTableContent({
  rowDataReducerState,
  languageSpecificColDefs,
  dataToDisplay,
  tableHeaderProps,
  renderField,
  getFieldDisplayValue,
  handleView,
  handleEdit,
  handleCopy,
  handleDelete,
  isEditDisabled,
  isDeleteDisabled,
  isSetupActionsLocked,
  gridFields,
  setupName,
  currentPage,
  setCurrentPage,
  isExportDialogOpen,
  headerLabel,
}: Readonly<SetupAgGridTableProps>): JSX.Element {
  const intl = useIntl();
  const { pageSizeOption, setPageSizeOption } = useUIStateContext();
  const gridRef = useRef<AgGridReact>(null);
  const tableContentRef = useRef<HTMLDivElement>(null);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(dataToDisplay.length);

  const isAgGridReady = !!gridApi;
  const rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const getFilteredRows = () => {
    const rows: RowData[] = [];
    gridApi?.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) rows.push(node.data);
    });
    return rows;
  };

  const tableHeaderColumns: ColDef[] = useMemo(() => {
    const baseCols: ColDef[] = languageSpecificColDefs
      .filter((col) => gridFields.has(col.field as string))
      .map((col) => {
        const field = col.field as string;

        return {
          ...col,
          headerTooltip: col.headerName,
          valueGetter: (params) => getFieldDisplayValue(field, params.data),
          cellRenderer: (params: ICellRendererParams) => (
            <TruncatedCellRenderer>
              {renderField(field, params.data?.[field], params.data)}
            </TruncatedCellRenderer>
          ),
        };
      });

    baseCols.push({
      headerName: "Actions",
      field: "actions",
      suppressMovable: true,
      width: 70,
      minWidth: 70,
      maxWidth: 85,
      filter: false,
      resizable: false,
      sortable: false,
      pinned: "right",
      // eslint-disable-next-line
      cellRenderer: ActionCellRenderer as any,
      cellRendererParams: {
        rowDataList: dataToDisplay,
        setupName,
        isEditDisabled,
        isDeleteDisabled,
        isSetupActionsLocked,
        handleView,
        handleEdit,
        handleCopy,
        handleDelete,
      },
    });

    return baseCols;
  }, [
    languageSpecificColDefs,
    gridFields,
    renderField,
    getFieldDisplayValue,
    setupName,
    isEditDisabled,
    isDeleteDisabled,
    isSetupActionsLocked,
    handleView,
    handleEdit,
    handleCopy,
    handleDelete,
  ]);

  const handleFirstDataRender = useCallback(
    (params: FirstDataRenderedEvent) => {
      params.api.sizeColumnsToFit();
    },
    [],
  );

  const handleGridReady = useCallback(() => {
    const api = gridRef.current?.api ?? null;
    setGridApi(api);

    const total = api?.paginationGetTotalPages() ?? 1;
    const restorePage = Math.min(currentPage, total);

    setTimeout(() => {
      api?.paginationGoToPage(restorePage - 1);
    }, 0);
  }, [currentPage]);
  // Resize AG Grid whenever the table container changes size.
  useEffect(() => {
    if (
      !tableContentRef.current ||
      !gridApi ||
      typeof ResizeObserver === "undefined"
    ) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        gridApi.sizeColumnsToFit();
      });
    });

    resizeObserver.observe(tableContentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [gridApi]);

  const handlePaginationChanged = () => {
    if (!gridApi) return;

    setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
    setTotalPages(gridApi.paginationGetTotalPages());
    setTotalRows(gridApi.getDisplayedRowCount());

    const currentPageSize = gridApi.paginationGetPageSize();
    if (currentPageSize !== pageSizeOption.value) {
      const matchedOption = rowsPerPageOptions.find(
        (option) => option.value === currentPageSize,
      );
      if (matchedOption) setPageSizeOption(matchedOption);
    }
  };

  const paginationProps: TablePaginationProps = {
    ariaLabel: "Pagination",
    buttonLabels: {
      goToFirst: "Go to first",
      goToLast: "Go to last",
      goToNext: "Go to next",
      goToPrevious: "Go to previous",
    },
    className: "",
    dataTestId: `table-pagination`,
    defaultPaginationPageSize: pageSizeOption,
    gridApi,
    hideBorder: false,
    pageSizeDropdownId: `page-size-dropdown-table`,
    pageSummaryLabelId: `pagination-summary-label-table`,
    pageSummaryText: getPageSummaryText(
      totalPages === 0 ? 0 : currentPage,
      totalPages,
    ),
    rowsPerPageLabel: intl.formatMessage({
      id: "label.rowsPerPage",
      defaultMessage: "Rows per page:",
    }),
    rowsPerPageOptions,
    rowsSummaryText: getRowsSummaryText(
      currentPage,
      pageSizeOption.value,
      totalRows,
    ),
    showGoToFirstLastPage: true,
  };

  const rowHeight = 30;
  const tableContentHeight = rowHeight * DEFAULT_ROWS_PER_PAGE_OPTIONS[2].value;

  const defaultColDef = useMemo(
    () => ({
      filter: true,
      sortable: true,
      resizable: true,
      flex: 1,
      autoHeight: true,
      comparator: numberFirstSort,
    }),
    [],
  );

  return (
    <>
      <div>
        <ARMigrationToast dataToDisplay={dataToDisplay} />

        <TableContainer agGridVersion="31.3.4">
          <TableHeader
            {...tableHeaderProps}
            headerTitle={`${headerLabel} (${gridApi?.getDisplayedRowCount() ?? dataToDisplay.length})`}
          />
          <TableHeaderContent schema={rowDataReducerState.schema} />
          <TableContent
            data-testid="table-content"
            ref={tableContentRef}
            style={{
              height: `${tableContentHeight}px`,
              width: "100%",
            }}
          >
            <AgGridReact
              className="ag-theme-balham"
              defaultColDef={defaultColDef}
              columnDefs={tableHeaderColumns}
              tooltipShowMode="whenTruncated"
              enableBrowserTooltips={true}
              onFirstDataRendered={handleFirstDataRender}
              onGridReady={handleGridReady}
              rowData={dataToDisplay}
              onPaginationChanged={handlePaginationChanged}
              pagination={true}
              paginationPageSize={pageSizeOption.value}
              suppressPaginationPanel
              ref={gridRef}
            />
          </TableContent>

          {isAgGridReady && (
            <TablePagination {...paginationProps} hideBorder={false} />
          )}
        </TableContainer>
      </div>

      {isExportDialogOpen && (
        <ExportRecordsDialog
          rowDataList={getFilteredRows()}
          rowDataReducerState={rowDataReducerState}
        />
      )}
    </>
  );
}
